import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";

import { purple } from "../../config";
import {
  LabelAndColor,
  isLabelAndColor,
  answerStatsTypes,
  BubbleStatsValues,
  BarStatsValues,
  BubbleStatsConfig,
  BarStatsConfig,
  isBubbleStatsValues,
  isBarStatsValues,
  AnswerStat,
  StatConfig,
  StatValues,
  isBarChartStatsValues,
  BarChartStatsConfig,
  BarChartStatsValues,
  isWordCloudStatValues,
  WordCloudStatsValues,
  WordCloudStatsConfig,
} from "../../interfaces/answerStats.interface";
import useCurrentAnswerStat from "../../hooks/useCurrentAnswerStat";
import { LangContext } from "../../contexts/langContext";
import { isTranslationData } from "@common";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const notAnsweredLabelAndColor = {
  label: "forTeachers.notAnswered",
  brightColor: grey[200],
  mainColor: grey[400],
  darkColor: grey[500],
};

const AnswerStats: React.FC<{
  values?: StatValues;
  config?: StatConfig;
  justStats?: boolean;
  useCurrentStat?: boolean;
  useUnansweredAmount?: boolean;
  showFooter?: boolean;
}> = ({
  values,
  config,
  justStats = false,
  useCurrentStat = true,
  useUnansweredAmount = true,
  showFooter = true,
}) => {
  const currentAnswerStat = useCurrentAnswerStat();
  const translatePageText = useTranslatePageText();

  if (useCurrentStat === true && currentAnswerStat !== undefined) {
    values = currentAnswerStat.stats.values;
    config = currentAnswerStat.stats.config;
  }

  if (values === undefined || config === undefined) return null;

  let statsComponent = null;

  if (config.type === answerStatsTypes.bubbles && isBubbleStatsValues(values)) {
    statsComponent = (
      <Bubbles
        currentAnswerStat={currentAnswerStat}
        values={values}
        config={config}
        useUnansweredAmount={useUnansweredAmount}
      />
    );
  } else if (config.type === answerStatsTypes.horizontalBar && isBarStatsValues(values)) {
    statsComponent = (
      <FullWidthBar
        values={values}
        config={config}
        currentAnswerStat={currentAnswerStat}
        useUnansweredAmount={useUnansweredAmount}
      />
    );
  } else if (config.type === answerStatsTypes.barChart && isBarChartStatsValues(values)) {
    statsComponent = (
      <BarChart
        barStatsValues={values}
        barStatsConfig={config}
        currentAnswerStat={currentAnswerStat}
        useUnansweredAmount={useUnansweredAmount}
      />
    );
  } else if (config.type === answerStatsTypes.wordCloud && isWordCloudStatValues(values)) {
    statsComponent = (
      <WordCloud
        values={values}
        config={config}
        currentAnswerStat={currentAnswerStat}
        useUnansweredAmount={useUnansweredAmount}
      />
    );
  } else {
    console.error("Can't find the stat component. Perhaps there is a mismatch?");
  }

  if (justStats || currentAnswerStat === undefined) return statsComponent;

  return (
    <Box width="100%" bgcolor={purple[600]} borderRadius={2} marginBottom={2} overflow="hidden">
      <Box
        width="100%"
        textAlign="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box textAlign="center" bgcolor={purple[400]}>
          <Typography variant="h6" margin={0.5} color={grey[100]}>
            {translatePageText("forTeachers.howPlayersAnswered")}
          </Typography>
        </Box>
        <Box
          p={2}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {statsComponent}
        </Box>
      </Box>
    </Box>
  );
};

const WordCloud: React.FC<{
  values: WordCloudStatsValues;
  config: WordCloudStatsConfig;
  currentAnswerStat: AnswerStat | undefined;
  useUnansweredAmount: boolean;
}> = ({ values, config, currentAnswerStat, useUnansweredAmount }) => {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignSelf: "flex-start",
        width: "100%",
        justifyContent: "center",
      }}
    >
      {values.comments.map((comment) => (
        <div
          key={comment.text}
          style={{
            background: purple[100],
            borderRadius: 18,
            padding: 12,
            margin: 4,
            color: config.textColor,
          }}
        >
          {comment.text}
        </div>
      ))}
    </div>
  );
};

const Bubbles: React.FC<{
  values: BubbleStatsValues;
  config: BubbleStatsConfig;
  currentAnswerStat: AnswerStat | undefined;
  useUnansweredAmount: boolean;
}> = ({ values, config, currentAnswerStat, useUnansweredAmount }) => {
  const maxBubblesDiam = 200;
  const minBubblesDiam = 80;
  let val1 = values.val1;
  const val2 = values.val2;
  const val3 = values.val3;

  if (currentAnswerStat && useUnansweredAmount === true) {
    val1 = currentAnswerStat.amountNotAnswered;
  }

  const getBubbleDiam = (val: number) =>
    (maxBubblesDiam - minBubblesDiam) * (val / (val1 + val2 + val3 + 0.0000001)) + minBubblesDiam;
  const val1diam = getBubbleDiam(val1);
  const val2diam = getBubbleDiam(val2);
  const val3diam = getBubbleDiam(val3);

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
      style={{
        transform: window.innerWidth < 500 ? "scale(0.8)" : "scale(1)",
        transition: "1s all ease",
      }}
    >
      <Bubble labelAndColor={config.labelAndColorForVal1} diameter={val1diam} val={val1} />
      <Box display="flex" marginY={2} justifyContent="center">
        <>
          <Bubble labelAndColor={config.labelAndColorForVal2} diameter={val2diam} val={val2} />
          <Box width={16} height={16}></Box>
          <Bubble labelAndColor={config.labelAndColorForVal3} diameter={val3diam} val={val3} />
        </>
      </Box>
    </Box>
  );
};

const Bubble: React.FC<{
  labelAndColor: LabelAndColor;
  val: number;
  diameter: number;
}> = ({ labelAndColor, val, diameter }) => {
  const translatePageText = useTranslatePageText();
  return (
    <Box
      borderRadius={1000}
      width={diameter}
      height={diameter}
      display="flex"
      justifyContent="center"
      alignItems="center"
      style={{
        transition: "1s ease all",
        background: `radial-gradient(61.34% 61.34% at 66.27% 38.66%, ${labelAndColor.brightColor} 0%, ${labelAndColor.mainColor} 50.15%, ${labelAndColor.darkColor} 100%)`,
      }}
    >
      <Box color={labelAndColor.darkColor} style={{ filter: "brightness(50%)" }} textAlign="center">
        <Typography fontWeight="bold" style={{ maxWidth: 50, lineHeight: 1, fontSize: 13 }}>
          {translatePageText(labelAndColor.label)}
        </Typography>
        <Typography marginTop="0rem">{val}</Typography>
      </Box>
    </Box>
  );
};

const BarChart: React.FC<{
  barStatsValues: BarChartStatsValues;
  barStatsConfig: BarChartStatsConfig;
  currentAnswerStat: AnswerStat | undefined;
  useUnansweredAmount: boolean;
}> = ({ barStatsValues, barStatsConfig, currentAnswerStat, useUnansweredAmount }) => {
  const { translateTranslationData, translatePageText } = useContext(LangContext);

  const values: Array<number> = Object.values(barStatsValues);
  const config: Array<LabelAndColor> = Object.values(barStatsConfig).filter(isLabelAndColor);

  if (useUnansweredAmount && currentAnswerStat?.amountNotAnswered !== undefined) {
    values.unshift(currentAnswerStat.amountNotAnswered);
    config.unshift(notAnsweredLabelAndColor);
  }

  for (let i = config.length - 1; i >= 0; i--) {
    const val = config[i];
    if (isLabelAndColor(val)) {
      let label = translatePageText(translatePageText(config[i].label));
      if (isTranslationData(label)) {
        label = translateTranslationData(label);
      }

      if (label === "") {
        config.splice(i, 1);
        values.splice(i, 1);
      }
    }
  }

  const totalAmount = values.reduce((partialSum, a) => partialSum + a, 0);

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "end", width: "100%", height: 180, marginTop: 32 }}
      >
        {values.map((val: number, index: number) => {
          if (index >= config.length) return null;

          let label = translatePageText(config[index].label);
          if (isTranslationData(label)) {
            label = '"' + translateTranslationData(label) + '"';
          }
          return (
            <div
              style={{
                transition: "all ease 0.5s",
                height: "calc(" + (val / totalAmount) * 100 + "% + 16px)",
                background: config[index].mainColor,
                flex: 1,
                margin: "0 3px",
                borderRadius: "4px 4px 0 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all ease 0.5s",
                  width: "100%",
                  height: "100%",
                  background:
                    val !== 0
                      ? "radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 80%)"
                      : "none",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  style={{
                    textAlign: "center",
                    margin: 0,
                    lineHeight: 0.9,
                    transition: "all ease 0.5s",
                    color: config[index].mainColor,
                    filter: "brightness(30%)",
                  }}
                >
                  {val}
                </Typography>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", width: "100%", paddingTop: 10 }}>
        {values.map((val: number, index: number) => {
          if (index >= config.length) return null;

          let label = translatePageText(config[index].label);
          if (isTranslationData(label)) {
            label = translateTranslationData(label);
          }
          return (
            <Typography
              variant="caption"
              fontWeight="bold"
              style={{
                flex: 1,
                margin: "0 8px",
                textAlign: "center",
                color: "white",
                lineHeight: 0.9,
                transition: "all ease 0.5s",
              }}
            >
              {label}
            </Typography>
          );
        })}
      </div>
    </div>
  );
};

const FullWidthBar: React.FC<{
  values: BarStatsValues;
  config: BarStatsConfig;
  currentAnswerStat: AnswerStat | undefined;
  useUnansweredAmount: boolean;
}> = ({ values, config, currentAnswerStat, useUnansweredAmount }) => {
  const { translateTranslationData, translatePageText } = useContext(LangContext);

  const valueArray: Array<number> = Object.values(values);
  const labelsAndColors: Array<LabelAndColor> = Object.values(config).filter(isLabelAndColor);

  if (useUnansweredAmount && currentAnswerStat?.amountNotAnswered) {
    labelsAndColors.unshift(notAnsweredLabelAndColor);
    valueArray.unshift(currentAnswerStat.amountNotAnswered);
  }

  const totalAmount = valueArray.reduce((partialSum, a) => partialSum + a, 0);

  if (totalAmount === 0) {
    return (
      <div
        style={{
          overflow: "hidden",
          borderRadius: 100,
          position: "relative",
          width: "100%",
          height: 20,
          background: grey[400],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          fontWeight="bold"
          style={{
            fontSize: 13,
            margin: 0,
            transition: "all ease 0.5s",
            filter: "brightness(20%)",
          }}
        >
          Inga svar
        </Typography>
      </div>
    );
  }

  return (
    <div
      style={{
        overflow: "hidden",
        borderRadius: 100,
        position: "relative",
        width: "100%",
        height: 20,
        display: "flex",
      }}
    >
      {valueArray.map((val: number, index) => {
        if (index >= labelsAndColors.length) return null;

        let label = translatePageText(labelsAndColors[index].label);
        if (isTranslationData(label)) {
          label = '"' + translateTranslationData(label) + '"';
        }
        return (
          <div
            style={{
              transition: "all ease 0.5s",
              height: "100%",
              width: (val / totalAmount) * 100 + "%",
              background: labelsAndColors[index].mainColor,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all ease 0.5s",
                width: "100%",
                height: "100%",
                background:
                  val !== 0
                    ? "radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 80%)"
                    : "none",
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                style={{
                  textAlign: "center",
                  margin: 0,
                  lineHeight: 0.9,
                  transition: "all ease 0.5s",
                  opacity: val === 0 ? 0 : 1,
                  filter: "brightness(20%)",
                }}
              >
                {val + " " + label}
              </Typography>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnswerStats;
